import { Box, Container, Grid } from "@eworkit/iris";
import { isNil } from "lodash-es";
import React, { FC, useCallback } from "react";
import { useRouter } from "router";
import { ensure, ensureArray, TType } from "types";

import { IURLSearchParams, IURLSortConfig, urlSearchParamsConfig, urlSortConfigType } from "@/core/url";
import {
  fetchPublicProfileNewsList,
  IPageablePublicProfileNews,
  IPublicProfileNews,
  PublicProfile,
} from "@/data/public-profiles";
import { TPageableFetchFunction, useMetaPageableFetcher } from "@/hooks/http";

import { RecentJobsWidget } from "../RecentJobsWidget";
import { PublicProfileNewsList } from "./PublicProfileNewsList";

export interface IPublicProfileNewsListSearchParams extends IURLSearchParams {}

const configPublicProfileNewsListSearchParamsType: TType<IPublicProfileNewsListSearchParams> = {
  ...urlSearchParamsConfig,
  sortConfig: v => (isNil(v) ? [] : ensureArray(urlSortConfigType)(v)),
};

const publicProfileNewsListSearchParamsType = ensure(configPublicProfileNewsListSearchParamsType);

interface ClientPageNewsTabProps {
  publicProfile: PublicProfile;
  isPublic?: boolean;
}

export const ClientPageNewsTab: FC<ClientPageNewsTabProps> = ({ publicProfile, isPublic }) => {
  const { searchParams: meta, replaceSearchParams: changeMeta } = useRouter({
    ensureSearchParams: publicProfileNewsListSearchParamsType,
  });

  const fetchNews: TPageableFetchFunction<IPublicProfileNews, IPageablePublicProfileNews, {}, IURLSortConfig[]> =
    useCallback(
      (params, options) => {
        return fetchPublicProfileNewsList(
          {
            ...params,
            publicProfileId: publicProfile.id,
          },
          options,
        );
      },
      [publicProfile],
    );

  // NOTE: we currently fetch all the news, pagination will be covered later
  const newsFetcher = useMetaPageableFetcher(
    fetchNews,
    {
      meta: {
        ...meta,
        size: 999999,
        sortConfig: [
          {
            sortBy: "publicationDate",
            order: "DESC",
          },
        ],
        filtersConfig: {
          status: "ACTIVE",
        },
      },
      changeMeta,
    },
    [meta, changeMeta],
  );

  if (!newsFetcher.payload) {
    return null;
  }

  const news = newsFetcher.payload.content.map(i => i.data);

  return (
    <Container>
      <Box px={[null, null, null, "sm"]}>
        <Grid columns={["1fr", null, null, "repeat(12, 1fr)"]}>
          <Box gridColumnStart={1} gridColumnEnd={[2, null, null, 9]} gridRowStart={1} gridRowEnd={2}>
            <PublicProfileNewsList publicProfile={publicProfile} news={news} isPublic={isPublic} />
          </Box>

          <Box
            gridColumnStart={[1, null, null, 9]}
            gridColumnEnd={[1, null, null, 13]}
            gridRowStart={[2, null, null, 1]}
            gridRowEnd={[3, null, null, 2]}>
            <RecentJobsWidget publicProfileIds={[publicProfile.id]} slug={publicProfile.slug} isPublic={isPublic} />
          </Box>
        </Grid>
      </Box>
    </Container>
  );
};
